import Flickity from 'flickity';

import debounce from '../util/debounce';
import {register} from '../vendor/theme-scripts/theme-sections';

const sections = {};

const selectors = {
  shopifySection: '.shopify-section',
  slideshow: '#slideshow-',
  slideshowSlide: '.slideshow__slide',
  slideshowSlideImg: '.slide-image-img',
  headerContent: '.header__content',
  heroContent: '.hero__content',
  heroContentWrapper: '.hero__content__wrapper',
  heroContentTransparent: 'hero__content--transparent',
  slideshowPrev: '.slideshow__arrow--prev',
  slideshowNext: '.slideshow__arrow--next',
  sliderDots: '.flickity-page-dots',
  dataAspectRatio: 'data-aspectratio',
  dataDots: 'data-dots',
  dataAutoplay: 'data-autoplay',
  dataAutoplaySpeed: 'data-speed',
  dataColor: 'data-color',
  dataSlideIndex: 'data-slide-index',
  textDark: 'text-dark',
  textLight: 'text-light',
  classIsSelected: 'is-selected',
  checkTrueString: 'true',
  transparentWrapper: 'transparent__wrapper',
  flickityEnabled: 'flickity-enabled',
};

class Slideshow {
  constructor(section) {
    this.container = section.container;
    this.sectionId = section.id;
    this.slideshow = this.container.querySelector(selectors.slideshow + this.sectionId);
    this.parentContainer = this.container.closest(selectors.shopifySection);
    this.parentContainerIdx = Array.from(this.parentContainer.parentElement.children).indexOf(this.parentContainer);
    this.heroContentWrapper = this.slideshow.querySelector(selectors.heroContentWrapper);
    this.slideshowPrev = this.container.querySelector(selectors.slideshowPrev);
    this.slideshowNext = this.container.querySelector(selectors.slideshowNext);
    this.flkty = null;

    this.init();
  }

  init() {
    const self = this;

    const autoPlay = this.slideshow.getAttribute(selectors.dataAutoplay) === selectors.checkTrueString;
    let autoPlaySpeed = false;
    const showDots = this.slideshow.getAttribute(selectors.dataDots) === selectors.checkTrueString;

    if (autoPlay) {
      autoPlaySpeed = parseInt(this.slideshow.getAttribute(selectors.dataAutoplaySpeed));
    }

    this.setMinHeight();

    this.flkty = new Flickity(this.slideshow, {
      autoPlay: autoPlaySpeed,
      prevNextButtons: false,
      contain: true,
      pageDots: showDots,
      adaptiveHeight: true,
      wrapAround: true,
      on: {
        ready: function () {
          self.slideActions(this.slider, this.element);
        },
      },
    });

    this.slideshowPrev.addEventListener('click', function (e) {
      e.preventDefault();

      self.flkty.previous(true);
    });

    this.slideshowNext.addEventListener('click', function (e) {
      e.preventDefault();

      self.flkty.next(true);
    });

    this.flkty.on('change', function (index) {
      self.slideActions(this.slider, this.element);
    });

    window.addEventListener('resize', debounce(self.setMinHeight.bind(self), 250));
  }

  slideActions(slider, element) {
    const currentSlide = slider.querySelector(`.${selectors.classIsSelected}`);
    const currentSlideColor = currentSlide.getAttribute(selectors.dataColor);

    this.slideshowPrev.classList.remove(selectors.textLight, selectors.textDark);
    this.slideshowNext.classList.remove(selectors.textLight, selectors.textDark);

    this.slideshowPrev.classList.add(currentSlideColor);
    this.slideshowNext.classList.add(currentSlideColor);

    this.setDotsColor(element, currentSlideColor);

    this.container.classList.remove(selectors.transparentWrapper);
    if (currentSlide.querySelector(selectors.heroContentWrapper).classList.contains(selectors.heroContentTransparent)) {
      this.container.classList.add(selectors.transparentWrapper);
    }

    this.setMinHeight();
  }

  setDotsColor(slider, currentColor) {
    const dots = slider.querySelector(selectors.sliderDots);

    if (dots) {
      dots.classList.remove(selectors.textLight, selectors.textDark);
      dots.classList.add(currentColor);
    }
  }

  setMinHeight() {
    const slideshow = this.slideshow;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isFirstSection = this.parentContainerIdx == 0;
    const headerContent = document.querySelector(selectors.headerContent);
    let minHeight = 0;
    let headerContentHeight = 0;
    if (headerContent) {
      headerContentHeight = parseFloat(getComputedStyle(headerContent, null).height.replace('px', ''));
    }

    if (isFirstSection) {
      const selectedSlide = slideshow.querySelector(`.${selectors.classIsSelected} ${selectors.heroContent}`);
      let sliderHeight = 0;
      const headerHeight = windowWidth >= 750 ? headerContentHeight : 0;
      if (selectedSlide) {
        sliderHeight = selectedSlide.offsetHeight;
      }
      const extraPadding = windowWidth >= 750 ? 120 : 40;
      minHeight = headerHeight + sliderHeight + extraPadding;

      this.heroContentWrapper.style.paddingTop = `${headerHeight}px`;
      this.heroContentWrapper.style.marginTop = `0px`;
    }

    slideshow.querySelectorAll(selectors.slideshowSlide).forEach((element) => {
      const slideImageImg = element.querySelector(selectors.slideshowSlideImg);
      let slideAspectRatio = '';
      if (slideImageImg && slideImageImg.hasAttribute(selectors.dataAspectRatio)) {
        slideAspectRatio = slideImageImg.getAttribute(selectors.dataAspectRatio);
      }

      let slideTextContentHeight = 0;
      const slideTextContent = element.querySelector(selectors.heroContent);
      if (slideTextContent) {
        slideTextContentHeight = slideTextContent.offsetHeight;
      }

      const slideWidth = parseInt(getComputedStyle(element, null).width.replace('px', ''));
      let slideHeight = parseInt(slideWidth / slideAspectRatio) || 0;
      const isCurrentSlide = element.classList.contains(selectors.classIsSelected);

      if (isFirstSection && slideHeight < minHeight) {
        slideHeight = minHeight;
      }

      if (slideTextContentHeight > slideHeight) {
        slideHeight = slideTextContentHeight;
      }

      element.style.minHeight = `${slideHeight}px`;
      element.querySelector(selectors.heroContentWrapper).style.minHeight = `${slideHeight}px`;

      if (isCurrentSlide) {
        slideshow.parentElement.style.minHeight = `${slideHeight}px`;
      }
    });
  }

  onUnload() {
    this.flkty.destroy();
  }

  onBlockSelect(evt) {
    // Ignore the cloned version
    const slide = this.slideshow.querySelector(`${selectors.slideshowSlide}--${evt.detail.blockId}`);
    const slideIndex = parseInt(slide.getAttribute(selectors.dataSlideIndex));

    // Go to selected slide, pause autoplay
    this.slideshow.classList.add(selectors.classIsSelected);
    this.flkty.select(slideIndex);
    this.flkty.stopPlayer();
  }

  onBlockDeselect(evt) {
    this.slideshow.classList.remove(selectors.classIsSelected);
    this.flkty.playPlayer();
  }
}

const SlideshowSection = {
  onLoad() {
    sections[this.id] = new Slideshow(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
  onBlockSelect(e) {
    sections[this.id].onBlockSelect(e);
  },
  onBlockDeselect(e) {
    sections[this.id].onBlockDeselect(e);
  },
};

register('slideshow', SlideshowSection);
